/**
 * What a Chat window needs of its repo's graph — which Claude worktrees are out,
 * the task each was started for, and the task an old worktree's tag was left by —
 * kept per repo, so a Chat keeps its prompt box and status chip while the git
 * panel shows another repo (or Global). The panel feeds in every graph it reads;
 * a Chat of a repo the panel is not on reads its own (`loadChatTrees`).
 *
 * Module state, like the windows: leaving the tmux view and coming back keeps it.
 */
import { reactive } from "vue";
import { GIT_READ_TIMEOUT_MS, api } from "./api.js";
import type { GitGraph } from "@shared/gitGraph.js";

export interface ChatTrees {
  /** Claude worktrees out, by name, and the task each was started for. */
  out: Map<string, string | null>;
  /** `worktree/…` tag → the task its worktree was left by. */
  tagTasks: Record<string, string>;
  readAt: number;
}

const held = reactive(new Map<string, ChatTrees>());

/** A graph read: what the Chats of its repo go by from now on. An older read
 *  than the one held (a slow fetch landing late) changes nothing. */
export function noteGraph(graph: GitGraph): void {
  const had = held.get(graph.repo);
  if (had && graph.readAt < had.readAt) return;
  const out = new Map<string, string | null>();
  for (const w of graph.worktrees ?? []) if (w.name) out.set(w.name, w.taskId ?? null);
  held.set(graph.repo, { out, tagTasks: graph.tagTasks ?? {}, readAt: graph.readAt });
}

export function chatTrees(repo: string): ChatTrees | null {
  return held.get(repo) ?? null;
}

const loading = new Set<string>();
/** Reads a repo's graph for its Chats (the server's copy of a few seconds ago will do). */
export async function loadChatTrees(repo: string): Promise<void> {
  if (loading.has(repo)) return;
  loading.add(repo);
  try {
    const { graph } = await api.get<{ graph: GitGraph }>(`/api/tmux/git/${encodeURIComponent(repo)}`, { timeoutMs: GIT_READ_TIMEOUT_MS });
    noteGraph(graph);
  } catch {
    // Kept as it was: the next look tries again.
  } finally {
    loading.delete(repo);
  }
}

/** For tests, and sign-out: hold nothing. */
export function forgetChatTrees(): void {
  held.clear();
}
